import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Stack,
  Title,
  Text,
  Card,
  SimpleGrid,
  Group,
  Badge,
  Skeleton,
  ThemeIcon,
  Anchor,
  TextInput,
} from '@mantine/core';
import {
  IconArrowLeft,
  IconBookmarks,
  IconChevronRight,
  IconFileText,
  IconSearch,
} from '@tabler/icons-react';
import { usePublicKnowledgeBaseService } from '@actschurch/ccms-sdk';
import type { KBCategoryModelView } from '@actschurch/ccms-sdk';
import { RichTextPreview } from '@/components/common/RichText';
import { VisibilityBadge } from '@/components/common/VisibilityBadge';
import { kbArticleHref, matchesSlug } from '@/utils/slug';

/**
 * KB category detail — lists every article in the category whose slug
 * matches the URL. Slug is built client-side from the category name
 * (KBCategory has no Slug column today); we resolve back to a category
 * by matching <c>slugify(name)</c> against the URL parameter.
 *
 * Data path: the public KB surface enforces status + visibility + audience
 * server-side, so this page works for anonymous visitors *and* signed-in
 * users — each sees only what they're entitled to. No category-by-slug
 * endpoint exists yet, so we list visible KBs + their categories and resolve
 * the slug locally; the fan-out is bounded by the number of KBs the caller
 * may see (a handful).
 */
export default function KBCategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const publicKb = usePublicKnowledgeBaseService();

  const [q, setQ] = useState('');

  const { data: resolved, isLoading: loadingCategory } = useQuery({
    queryKey: ['public-kb', 'category-by-slug', slug],
    queryFn: async () => {
      if (!slug) return null;
      const kbs = await publicKb.listKnowledgeBases();
      const categoriesPerKb = await Promise.all(
        kbs.map((kb) => publicKb.listCategoriesForKnowledgeBase(kb.knowledgeBaseID)),
      );
      const flat: KBCategoryModelView[] = categoriesPerKb.flat();
      return flat.find((c) => matchesSlug(slug, { name: c.name })) ?? null;
    },
    enabled: !!slug,
  });

  const category = resolved ?? null;

  const { data: articles = [], isLoading: loadingArticles } = useQuery({
    queryKey: ['public-kb', 'category-articles', category?.kbCategoryID],
    queryFn: () =>
      category
        ? publicKb.listArticlesInCategory(category.kbCategoryID)
        : Promise.resolve([]),
    enabled: !!category?.kbCategoryID,
  });

  // Local filter — server already applied visibility, so this is purely a
  // typeahead over the visible-to-this-viewer list.
  const visibleArticles = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return articles;
    return articles.filter(
      (a) =>
        (a.title ?? '').toLowerCase().includes(needle) ||
        (a.summary ?? '').toLowerCase().includes(needle) ||
        (a.tags ?? '').toLowerCase().includes(needle),
    );
  }, [articles, q]);

  if (loadingCategory) {
    return (
      <Stack gap="md">
        <Skeleton height={32} width={300} />
        <Skeleton height={120} radius="lg" />
      </Stack>
    );
  }

  if (!category) {
    return (
      <Stack gap="md">
        <Anchor component={Link} to="/kb" size="sm" c="dimmed">
          <Group gap={4}><IconArrowLeft size={14} />Back to Knowledge Base</Group>
        </Anchor>
        <Card withBorder padding="xl" radius="lg">
          <Stack align="center" gap="sm">
            <IconFileText size={36} color="var(--mantine-color-dimmed)" />
            <Text c="dimmed">Category not found.</Text>
          </Stack>
        </Card>
      </Stack>
    );
  }

  return (
    <Stack gap="lg">
      <Anchor component={Link} to="/kb" size="sm" c="dimmed">
        <Group gap={4}><IconArrowLeft size={14} />Back to Knowledge Base</Group>
      </Anchor>

      <Card
        withBorder
        padding="lg"
        radius="lg"
        shadow="sm"
        style={{
          background: category.color
            ? `linear-gradient(135deg, ${category.color}22, transparent 70%)`
            : 'var(--mantine-color-body)',
        }}
      >
        <Group gap="md" align="flex-start">
          <ThemeIcon size={56} radius="md" variant="light" color={category.color ?? 'brand'}>
            <IconBookmarks size={28} />
          </ThemeIcon>
          <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
            <Title order={2}>{category.name}</Title>
            {/* Category descriptions are HTML strings from the rich-text
                editor; render the plain-text version so we don't show the
                raw <p style="…"> markup. */}
            <RichTextPreview html={category.description} c="dimmed" />
            <Group gap="xs" mt={4}>
              <Badge variant="light" color={category.color ?? 'brand'} radius="xl">
                {visibleArticles.length} article{visibleArticles.length === 1 ? '' : 's'}
              </Badge>
              {/* Visibility chip — Unlisted/Restricted; Public renders nothing. */}
              <VisibilityBadge
                visibilityName={category.visibilityName}
                visibilityColor={category.visibilityColor}
              />
            </Group>
          </Stack>
        </Group>
      </Card>

      <TextInput
        leftSection={<IconSearch size={16} />}
        placeholder="Filter articles in this category"
        value={q}
        onChange={(e) => setQ(e.currentTarget.value)}
        radius="xl"
      />

      {loadingArticles ? (
        <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="lg">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} height={170} radius="lg" />
          ))}
        </SimpleGrid>
      ) : visibleArticles.length === 0 ? (
        <Card withBorder padding="xl" radius="lg">
          <Stack align="center" gap="sm">
            <IconFileText size={36} color="var(--mantine-color-dimmed)" />
            <Text c="dimmed">
              {q.trim()
                ? `No articles match "${q.trim()}" in this category.`
                : 'No articles in this category yet.'}
            </Text>
          </Stack>
        </Card>
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="lg">
          {visibleArticles.map((a) => {
            const to = kbArticleHref(a);
            return (
              <Card
                key={a.kbArticleID}
                component={Link}
                to={to}
                withBorder
                padding="lg"
                radius="lg"
                style={{ textDecoration: 'none', color: 'inherit' }}
              >
                <Stack gap="xs">
                  <Group justify="space-between" align="flex-start" wrap="nowrap" gap="xs">
                    <Title order={5} lh={1.3} style={{ flex: 1 }}>
                      {a.title}
                    </Title>
                    <IconChevronRight size={16} color="var(--mantine-color-dimmed)" style={{ flex: '0 0 auto' }} />
                  </Group>
                  <RichTextPreview html={a.summary} size="sm" c="dimmed" lineClamp={3} />
                  {a.readingTimeMinutes != null && (
                    <Text size="xs" c="dimmed">
                      ~ {a.readingTimeMinutes} min read
                    </Text>
                  )}
                </Stack>
              </Card>
            );
          })}
        </SimpleGrid>
      )}
    </Stack>
  );
}
